import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import NotificationBell from "./admin/NotificationBell";
import {
  LayoutDashboard,
  Users,
  FileText,
  Send,
  FolderOpen,
  LogOut,
  Menu,
  X,
  Home,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Package,
  Target,
  Activity,
  BarChart3,
  TrendingUp,
  DollarSign,
  Calendar,
  Flame,
  Handshake
} from "lucide-react";

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();


  const navItems = [
    { name: "Dashboard", path: "/admin", icon: LayoutDashboard, roles: ["admin", "agent", "assistant"] },
    { name: "Priority Queue", path: "/admin/priority-queue", icon: Flame, roles: ["admin", "agent", "assistant"] },
    { name: "Leads", path: "/admin/leads", icon: Users, roles: ["admin", "agent", "assistant"] },
    { name: "Deal Inventory", path: "/admin/deals", icon: Package, roles: ["admin", "agent"] },
    { name: "Deal Intelligence", path: "/admin/intelligence", icon: BarChart3, roles: ["admin", "agent"] },
    { name: "Funnel Performance", path: "/admin/funnel", icon: TrendingUp, roles: ["admin", "agent"] },
    { name: "Lease Revenue", path: "/admin/leases", icon: DollarSign, roles: ["admin", "agent"] },
    { name: "Partners CRM", path: "/admin/partners", icon: Handshake, roles: ["admin", "agent", "assistant"] },
    { name: "Tour Availability", path: "/admin/tour-availability", icon: Calendar, roles: ["admin", "agent"] },
    { name: "Top Picks", path: "/admin/top-picks", icon: Target, roles: ["admin", "agent"] },
    { name: "Attribution", path: "/admin/attribution", icon: ShieldAlert, roles: ["admin", "agent"] },
    { name: "Drip Control", path: "/admin/drip", icon: Activity, roles: ["admin", "agent"] },
    { name: "Email Templates", path: "/admin/templates", icon: FileText, roles: ["admin", "agent"] },
    { name: "Campaigns", path: "/admin/campaigns", icon: Send, roles: ["admin", "agent"] },
    { name: "Content", path: "/admin/content", icon: FolderOpen, roles: ["admin", "agent"] },
    { name: "User Management", path: "/admin/users", icon: ShieldCheck, roles: ["admin"] },
  ].filter(item => item.roles.includes(user?.role || "assistant"));

  const isActive = (path) => {
    if (path === location.pathname) return true;
    // Highlight parent sections when viewing nested detail pages.
    if (path === "/admin/partners" && location.pathname.startsWith("/admin/partners/")) return true;
    return false;
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-[#0A2342] rounded-lg flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#0A2342]">Admin</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell variant="mobile" />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-slate-100"
            data-testid="admin-mobile-menu"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#0A2342] transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <Link to="/admin" className="flex items-center space-x-2" data-testid="admin-logo">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-[#0A2342]" />
              </div>
              <div>
                <span className="font-bold text-white block">Urban Nest Quest</span>
                <span className="text-xs text-slate-400">Admin Panel</span>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? "bg-[#DE6E4B] text-white"
                    : "text-slate-300 hover:bg-slate-700 hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="px-4 py-2 mb-2">
              <p className="text-sm text-slate-400">Logged in as</p>
              <p className="text-white font-medium truncate">{user?.name || user?.email}</p>
              <p className="text-xs text-[#DE6E4B] capitalize">{user?.role || "admin"}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-slate-300 hover:bg-red-500/20 hover:text-red-400 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
            <Link
              to="/"
              className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white transition-all mt-1"
              data-testid="admin-view-site"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">View Site</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop top-right bell — only visible on lg+ where mobile header is hidden. */}
        <div className="hidden lg:flex justify-end px-8 pt-4">
          <NotificationBell variant="desktop" />
        </div>
        <div className="p-4 md:p-8 lg:pt-2">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
